<template>
  <div class="flex flex-col gap-3">
    <p class="text-sm text-gray-500">
      <template v-if="!loading && !error && kpis">
        <b class="text-gray-900">{{ practiceCount }}</b> practices under <b class="text-gray-900">{{ team.length }}</b> managers
        · <b :class="kpis.high_severity ? 'text-red-dark' : 'text-gray-900'">{{ kpis.high_severity }}</b> high-severity gaps
        · <b :class="kpis.uncovered ? 'text-red-dark' : 'text-gray-900'">{{ kpis.uncovered }}</b> gaps with no plan
        · <b :class="kpis.overdue ? 'text-red-dark' : 'text-gray-900'">{{ kpis.overdue }}</b> overdue action items
      </template>
    </p>

    <div v-if="error" class="h-[500px] overflow-y-auto rounded-lg border border-red-light bg-red-tint p-4 text-sm text-red-dark">
      {{ error }}
      <button type="button" class="btn-link ml-2" @click="load">Retry</button>
    </div>

    <div v-else-if="loading" class="h-[500px] overflow-hidden rounded-lg border border-gray-200">
      <div v-for="n in 8" :key="n" class="flex items-center gap-3 border-b border-growth-50 px-4 py-3 animate-pulse">
        <div class="h-3 w-3 rounded bg-gray-100"></div>
        <div class="h-3 w-1/3 rounded bg-gray-100"></div>
        <div class="ml-auto h-3 w-1/2 rounded bg-gray-100"></div>
      </div>
    </div>

    <div v-else class="h-[500px] min-h-0 overflow-auto rounded-lg border border-gray-200">
      <table class="w-full table-fixed border-collapse text-sm">
        <colgroup>
          <col class="w-[4%]" />
          <col class="w-[32%]" />
          <col class="w-[10%]" />
          <col class="w-[10%]" />
          <col class="w-[9%]" />
          <col class="w-[9%]" />
          <col class="w-[9%]" />
          <col class="w-[9%]" />
          <col class="w-[8%]" />
        </colgroup>
        <thead class="sticky top-0 z-10 bg-growth-10">
          <tr class="border-b border-growth-50">
            <th v-for="h in headers" :key="h" class="px-3 py-2 text-left align-middle text-[11px] font-semibold uppercase tracking-wide text-growth-600">{{ h }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="row in team" :key="row.id ?? 'unassigned'">
            <tr class="cursor-pointer border-b border-growth-50 hover:bg-gray-50" @click="toggle(row)">
              <td class="py-2.5 pl-4">
                <font-awesome-icon :icon="['fas', open[row.id] ? 'chevron-down' : 'chevron-right']" class="h-3 w-3 text-gray-400" />
              </td>
              <td class="px-3 py-2.5">
                <span class="sensitive font-semibold text-growth-900">{{ row.name }}</span>
                <span v-if="row.role" class="ml-2 text-xs text-growth-600">{{ row.role }}</span>
              </td>
              <td class="px-3 py-2.5" :class="weight(row.practices)">{{ row.practices }}</td>
              <td class="px-3 py-2.5" :class="weight(row.c_open)">{{ row.c_open }}</td>
              <td class="px-3 py-2.5" :class="alert(row.c_high)">{{ row.c_high }}</td>
              <td class="px-3 py-2.5" :class="alert(row.uncovered)">{{ row.uncovered }}</td>
              <td class="px-3 py-2.5" :class="weight(row.a_open)">{{ row.a_open }}</td>
              <td class="px-3 py-2.5" :class="alert(row.a_overdue)">{{ row.a_overdue }}</td>
              <td class="px-3 py-2.5" :class="weight(row.meetings)">{{ row.meetings }}</td>
            </tr>
            <template v-if="open[row.id]">
              <tr v-for="child in row.children" :key="child.id" class="border-b border-growth-50 bg-growth-10">
                <td></td>
                <td class="px-3 py-2 pl-6">
                  <router-link :to="`/entity/${child.id}`" class="sensitive break-words text-growth-900 hover:underline">{{ child.name }}</router-link>
                </td>
                <td class="px-3 py-2 text-gray-400">—</td>
                <td class="px-3 py-2" :class="weight(child.c_open)">{{ child.c_open }}</td>
                <td class="px-3 py-2" :class="alert(child.c_high)">{{ child.c_high }}</td>
                <td class="px-3 py-2" :class="alert(child.uncovered)">{{ child.uncovered }}</td>
                <td class="px-3 py-2" :class="weight(child.a_open)">{{ child.a_open }}</td>
                <td class="px-3 py-2" :class="alert(child.a_overdue)">{{ child.a_overdue }}</td>
                <td class="px-3 py-2" :class="weight(child.meetings)">{{ child.meetings }}</td>
              </tr>
            </template>
          </template>
          <tr v-if="!team.length">
            <td colspan="9" class="px-5 py-6 text-sm text-growth-600">{{ entity_id ? 'No open gaps or action items under this entity.' : 'No linked entity for this user.' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { getScopedOpsSummary } from '@/services/api/engagementGraph'

export default {
  name: 'Portfolio_Rollup',
  props: {
    entity_id: { type: Number, default: null },
    sectionId: { type: String, default: null },
    expanded: { type: Boolean, default: true },
    fullWidth: { type: Boolean, default: false },
  },
  data() {
    return {
      team: [],
      kpis: null,
      loading: true,
      error: null,
      open: {},
      headers: ['', 'Manager / practice', 'Practices', 'Open gaps', 'High', 'No plan', 'Open actions', 'Overdue', 'Meetings YTD'],
    }
  },
  computed: {
    practiceCount() {
      return this.team.reduce((n, r) => n + r.practices, 0)
    },
  },
  watch: {
    entity_id() {
      this.load()
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      if (!this.entity_id) {
        this.team = []
        this.loading = false
        return
      }
      this.loading = true
      this.error = null
      this.open = {}
      const requested = this.entity_id
      try {
        const data = await getScopedOpsSummary({ scopeEntityId: requested })
        if (requested !== this.entity_id) return
        this.team = data.team
        this.kpis = data.kpis
      } catch (e) {
        this.error = e?.response?.data?.detail || 'Could not load your practices.'
      } finally {
        this.loading = false
      }
    },
    toggle(row) {
      this.open[row.id] = !this.open[row.id]
    },
    weight(v) {
      return v ? 'font-semibold text-growth-900' : 'text-gray-400'
    },
    alert(v) {
      return v ? 'font-semibold text-red-dark' : 'text-gray-400'
    },
  },
}
</script>
